import { computed, onMounted, onUnmounted, ref, shallowRef } from 'vue'
import type { UFPMMember, UFPMScoreEntry } from '../lib/types'

function todayISO(): string {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

export interface UFPMHistoryEntry extends UFPMScoreEntry {
  memberName: string
  memberId: string
}

function entryDate(entry: UFPMScoreEntry): string {
  return (entry.recordedDate || entry.date || '').slice(0, 10)
}

export function matchesHistoryDateRange(entry: UFPMScoreEntry, from: string, to: string): boolean {
  const d = entryDate(entry)
  if (!d) return false
  if (!from && !to) return true
  if (from && !to) return d === from
  if (!from && to) return d <= to
  const start = from <= to ? from : to
  const end = from <= to ? to : from
  return d >= start && d <= end
}

export function useUFPMHistory() {
  const members    = shallowRef<readonly UFPMMember[]>([])
  const dateFrom   = ref(todayISO())
  const dateTo     = ref('')
  const memberFilter = ref('')

  function refreshMembers(): void {
    members.value = window.ufpmTools?.getMembers?.() ?? []
  }
  function onMembersChanged(event: Event): void {
    const next = (event as CustomEvent).detail?.members as UFPMMember[] | undefined
    if (next) members.value = next
  }

  onMounted(() => { refreshMembers(); window.addEventListener('ptr:ufpm-members-changed', onMembersChanged) })
  onUnmounted(() => { window.removeEventListener('ptr:ufpm-members-changed', onMembersChanged) })

  const allEntries = computed<UFPMHistoryEntry[]>(() => {
    const out: UFPMHistoryEntry[] = []
    for (const m of members.value) {
      for (const entry of m.history ?? []) {
        out.push({ ...entry, memberId: m.id, memberName: m.fullName || m.lastName || 'Unknown' })
      }
    }
    // sort newest first by recordedDate or date
    out.sort((a, b) => {
      const da = a.recordedDate || a.date || ''
      const db = b.recordedDate || b.date || ''
      return db.localeCompare(da)
    })
    return out
  })

  const filteredEntries = computed<UFPMHistoryEntry[]>(() => {
    let list = allEntries.value
    const from = dateFrom.value.trim()
    const to = dateTo.value.trim()
    if (from || to) list = list.filter(e => matchesHistoryDateRange(e, from, to))
    const mf = memberFilter.value.trim()
    if (mf) list = list.filter(e => e.memberId === mf)
    return list
  })

  const memberOptions = computed(() =>
    (members.value as UFPMMember[]).map(m => ({ id: m.id, label: [m.rank, m.fullName].filter(Boolean).join(' ') }))
  )

  function clearDateFilters(): void {
    dateFrom.value = ''
    dateTo.value = ''
  }

  return {
    members,
    dateFrom,
    dateTo,
    memberFilter,
    allEntries,
    filteredEntries,
    memberOptions,
    todayISO,
    clearDateFilters,
  }
}
